import { useEffect, useState } from "react"
import { onAuthStateChanged, type User } from "firebase/auth"

import { auth } from "@/features/auth/providers/firebase"

import { astroTodayService, type AstroTodayRequestContext } from "../api/astro-today-service"
import type { AstroTab, AstroTodayData, AstroTodayResponse } from "../types"

interface AstroTodayState {
  data: AstroTodayData | null
  meta: AstroTodayResponse["meta"] | null
  loading: boolean
  error: boolean
}

interface CacheEntry {
  data: AstroTodayData
  meta: AstroTodayResponse["meta"]
  expiresAt: number
}

/** Base in-memory TTL — long enough to cover back-and-forth tab switching. */
const CACHE_TTL_MS = 5 * 60 * 1000

/**
 * Process-lived cache of Today responses, keyed by user + requested inputs, so
 * switching between tabs reuses the same daily payload instead of refetching.
 */
const responseCache = new Map<string, CacheEntry>()

const cacheKeyFor = (uid: string, tabsKey: string, contextKey: string): string =>
  `${uid}|${tabsKey}|${contextKey}`

const readCache = (key: string): CacheEntry | null => {
  const entry = responseCache.get(key)
  if (!entry) return null
  if (entry.expiresAt <= Date.now()) {
    responseCache.delete(key)
    return null
  }
  return entry
}

const writeCache = (
  key: string,
  data: AstroTodayData,
  meta: AstroTodayResponse["meta"],
): void => {
  // Don't cache responses that carry an inline per-tab error so a transient
  // failure can't stick around — let the next switch retry it.
  const hasTabError = Object.values(data).some(
    (value) => value != null && typeof value === "object" && "error" in value,
  )
  if (hasTabError) return

  // Daily data is valid until next_sync; cap the TTL by it so we never serve
  // yesterday's payload past local midnight.
  const nextSync = meta?.next_sync ? Date.parse(meta.next_sync) : NaN
  const ttlExpiry = Date.now() + CACHE_TTL_MS
  const expiresAt = Number.isNaN(nextSync) ? ttlExpiry : Math.min(ttlExpiry, nextSync)
  responseCache.set(key, { data, meta, expiresAt })
}

/**
 * Fetches the daily Today aggregate for the signed-in user. Re-runs when the
 * requested `tabs`/`context` change, serves a fresh in-memory cache hit without
 * a network call, and aborts the in-flight request on cleanup. `tabs` narrows
 * the request to the categories in view.
 */
export function useAstroToday(
  tabs?: AstroTab[],
  context?: AstroTodayRequestContext,
): AstroTodayState {
  const tabsKey = tabs?.join(",") ?? ""
  const contextKey = JSON.stringify(context ?? {})

  const [state, setState] = useState<AstroTodayState>(() => {
    const uid = auth.currentUser?.uid
    const hit = uid ? readCache(cacheKeyFor(uid, tabsKey, contextKey)) : null
    return hit
      ? { data: hit.data, meta: hit.meta, loading: false, error: false }
      : { data: null, meta: null, loading: true, error: false }
  })

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    const load = async (user: User | null) => {
      if (!user || !astroTodayService.isConfigured) {
        if (!cancelled) setState((s) => ({ ...s, loading: false }))
        return
      }

      const key = cacheKeyFor(user.uid, tabsKey, contextKey)
      const cached = readCache(key)
      if (cached) {
        if (!cancelled) {
          setState({ data: cached.data, meta: cached.meta, loading: false, error: false })
        }
        return
      }

      try {
        const token = await user.getIdToken()
        const res = await astroTodayService.getToday(
          token,
          tabsKey ? (tabsKey.split(",") as AstroTab[]) : undefined,
          context,
          controller.signal,
        )
        writeCache(key, res.data, res.meta)
        if (!cancelled) setState({ data: res.data, meta: res.meta, loading: false, error: false })
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error: true }))
      }
    }

    // onAuthStateChanged fires immediately with the current auth state, so it
    // already covers the signed-in case — no separate auth.currentUser call,
    // which would fire a duplicate request on mount.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) load(user)
    })

    return () => {
      cancelled = true
      controller.abort()
      unsubscribe()
    }
    // Re-run on the serialized inputs only; `context` identity is captured via
    // contextKey, so depending on the object would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey, contextKey])

  return state
}
